import { useIntl } from 'react-intl';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { FlyoutBody, FlyoutFooter, FlyoutHeader } from '@atlaskit/side-nav-items/flyout-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';

import { messages } from './messages';
import {
	OrganizationsFlyoutEmptyState,
	OrganizationsFlyoutErrorState,
	OrganizationsFlyoutLoadingState,
} from './organizations-flyout-states';
import { getOrganizationDisplayName } from './utils';

import type { Organization } from './types';

export type OrganizationsFlyoutContentProps = {
	error: string | null;
	onOrganizationSelect: (organization: Organization) => void;
	organizations: Organization[] | null;
	pending: boolean;
};

const OrganizationsFlyoutBodyContent = ({
	error,
	onOrganizationSelect,
	organizations,
	pending,
}: OrganizationsFlyoutContentProps) => {
	const intl = useIntl();

	if (organizations?.length) {
		return (
			<MenuList>
				{organizations.map((organization) => (
					<ButtonMenuItem
						key={organization.orgId}
						testId={`organizations-flyout-item-${organization.orgId}`}
						elemBefore={<OfficeBuildingIcon label="" color="currentColor" />}
						onClick={() => onOrganizationSelect(organization)}
					>
						{getOrganizationDisplayName(organization, intl)}
					</ButtonMenuItem>
				))}
			</MenuList>
		);
	}

	if (pending) {
		return <OrganizationsFlyoutLoadingState />;
	}

	if (error) {
		return <OrganizationsFlyoutErrorState />;
	}

	if (organizations === null) {
		return <OrganizationsFlyoutLoadingState />;
	}

	return <OrganizationsFlyoutEmptyState />;
};

export const OrganizationsFlyoutContent = ({
	error,
	onOrganizationSelect,
	organizations,
	pending,
}: OrganizationsFlyoutContentProps) => {
	const { formatMessage } = useIntl();

	return (
		<>
			<FlyoutHeader
				title={formatMessage(messages.flyoutTitle)}
				closeButtonLabel={formatMessage(messages.closeFlyoutLabel)}
			/>
			<FlyoutBody testId="organizations-flyout-body">
				<OrganizationsFlyoutBodyContent
					error={error}
					onOrganizationSelect={onOrganizationSelect}
					organizations={organizations}
					pending={pending}
				/>
			</FlyoutBody>
			<FlyoutFooter testId="organizations-flyout-footer">
				<MenuList>
					<ButtonMenuItem
						isDisabled
						elemBefore={<AlignTextLeftIcon label="" color="currentColor" />}
					>
						{formatMessage(messages.viewAllOrganizationsLabel)}
					</ButtonMenuItem>
				</MenuList>
			</FlyoutFooter>
		</>
	);
};
